import pick from 'lodash/pick';
import { ee } from '../../services/earth-engine';
import * as Metadata from '../../common/metadata';
import * as Indices from '../../common/indices';
import { getStandardSatellites } from '../../common/satellites';
export const getIntertidalShapes = function () {
  const dataset = ee.ImageCollection('UQ/murray/Intertidal/v1_1/global_intertidal');
  const intertidalImage = dataset.mosaic().select('classification');

  const visualization = {
    min: 1,
    max: 1,
    palette: ['yellow'],
  };

  const mapId = intertidalImage.getMap(visualization);
  const tileSource = new ee.layers.EarthEngineTileSource(mapId);

  return new ee.layers.ImageOverlay(tileSource);
};

export const addGridPosition = (satellite) => {
  return (element) => {
    const image = ee.Image(element);
    const rawPosition = satellite.computeGridPosition(image);
    const position = ee.Number(rawPosition.path).multiply(100).add(ee.Number(rawPosition.row));

    return image.set({ [Metadata.GRID_POSITION]: position });
  };
};

export const getDate = (image) => {
  return ee.Date(ee.Image(image).get(Metadata.TIME_START));
};

export const mergeFootprints = (collection) => {
  return ee.FeatureCollection(collection).geometry();
};

/**
 * @function retrieveExtremes
 * @description Get time from all images, sort and gets the earliest and latest images in the collection
 * @param {ee.ImageCollection} collection - collection of images
 * @returns {Object} earliest and latest images in the collection
 */
export const retrieveExtremes = (collection) => {
  const dateSorted = collection.sort(Metadata.TIME_START).toList(collection.size());
  return {
    earliest: getDate(ee.Image(dateSorted.get(0))),
    latest: getDate(ee.Image(dateSorted.get(-1))),
  };
};

export const mergeProperties = (collection) => {
  const sensorKey = 'SENSOR_ID';
  const spacecraftKey = 'SPACECRAFT_ID';

  const first = ee.List(collection.sort(Metadata.TIME_START).toList(1)).get(0);
  const firstImage = ee.Image(first);
  const date = getDate(first);
  const spacecraft = firstImage.get(spacecraftKey);

  return {
    [Metadata.NAME]: 'IMG_' + date.format(null, 'UTC'),
    [Metadata.FOOTPRINT]: mergeFootprints(collection),
    [Metadata.TIME_START]: date,
    [sensorKey]: 'TM',
    [spacecraftKey]: spacecraft,
  };
};

export const combineReducers = (...reducers) => {
  let group = null;

  for (const reducer of reducers) {
    if (group === null) {
      group = reducer;
    } else {
      group = group.combine(reducer, '', true);
    }
  }

  return group;
};

export const serializeList = (subject) => {
  return ee.String('"[').cat(ee.List(subject).join(',')).cat(']"');
};

export const generateVisualizationParams = (mission) => {
  const { red, green, blue } = mission.bands;
  return {
    ...mission.vizParams,
    bands: [red, green, blue],
  };
};

export const evaluate = (query) => {
  return query.evaluate.bind(query);
};

export const applyExpression = (image, expressionOrLabel, bands) => {
  const index = Indices.all().find((index) => index.label === expressionOrLabel);

  const expression = index !== undefined ? index.expression : expressionOrLabel;
  console.log('applyExpression (utils/index.js)', image);
  return image.expression(expression, {
    RED: image.select(bands.red),
    GREEN: image.select(bands.green),
    BLUE: image.select(bands.blue),
    NIR: image.select(bands.nir),
    SWIR: image.select(bands.swir),
  });
};

export const imageToKey = (image) => {
  return JSON.stringify(pick(image, 'id', 'version'));
};

/**
 * @function stripSuffix
 * @description Rename image's bands suffix
 * @param {ee.Image} img - Image to have its bands renamed
 * @param {string} suffix - Suffix to be removed from band names
 * *Called by:
 * - PeriodChooser.jsx - bathymetry acquisition step
 * @returns {ee.Image}  img - Image with renamed bands
 */
export const stripSuffix = (img, suffix) => {
  var bn = img.bandNames();
  var newBn = bn.map(function (b) {
    b = ee.String(b);
    return b.replace(suffix, '');
  });
  return img.select(bn, newBn);
};

export const getSatelliteCollection = (satellite) => {
  const { collectionSet } = satellite;
  if (Array.isArray(collectionSet)) {
    const [head] = collectionSet;
    return ee.ImageCollection(head);
  }
  return ee.ImageCollection(collectionSet);
};

export const simplify = (amount) => (feature) => {
  return ee.Feature(feature).simplify(amount);
};

/**
 * @function getSelectedSatellite
 * @description Get chosen satellite on the previous acquisition step
 * @returns {Object} - Information about the current chosen satellite
 */
export const getSelectedSatellite = (includesLandsat7 = false) => {
  const selectedSatelliteIndex = Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'));
  console.log('Selected satellite index from sessionStorage:', selectedSatelliteIndex);
  const satellite = getStandardSatellites(includesLandsat7)[[selectedSatelliteIndex]];
  return satellite;
};

export const GEE_ERROR_PATTERNS = [
  { patterns: [/no images/i, /empty collection/i, /collection.*empty/i], key: 'noImagesForPeriod' },
  { patterns: [/no bands/i, /band.*not found/i, /invalid.*band/i], key: 'filteredCollectionNoBands' },
  { patterns: [/timeout/i, /deadline exceeded/i, /timed out/i], key: 'errorTimeout' },
  { patterns: [/quota/i, /rate limit/i, /too many requests/i], key: 'errorQuota' },
  { patterns: [/permission/i, /not authorized/i, /access denied/i, /forbidden/i], key: 'errorPermission' },
  { patterns: [/not.*registered/i, /signup/i, /earthengine.*account/i], key: 'errorNotRegistered' },
  { patterns: [/request size/i, /request payload/i], key: 'errorRequestSize' },
  { patterns: [/memory/i, /out of memory/i, /computation.*too large/i], key: 'errorMemory' },
  { patterns: [/network/i, /connection/i, /failed to fetch/i], key: 'errorNetwork' },
];

/**
 * @constant GEE_ERROR_NAMESPACE
 * @description Where the wording for these failures lives. Under bathymetry for historical reasons
 * - it is the module that first needed them - but the text itself says nothing about bathymetry and
 * every module reports through it now.
 * @memberof module:SHARED
 */
const GEE_ERROR_NAMESPACE = 'forms.acquisition.3.bathymetry';

/**
 * @function matchGeeError
 * @description The plain-language wording for an Earth Engine failure, when the failure is one of
 * the kinds worth naming - out of quota, no images for the period, area too large, not signed up.
 * @param {Error|String} error - What Earth Engine threw.
 * @param {Function} t - Translation function.
 * @param {Object} [overrides] - Wording to use instead for a given matched key, as
 *  `{ matchedKey: replacementKey }`. The same failure can have different remedies depending on who
 *  provoked it: a memory limit reached by the analytic models means the area or the period is too
 *  large, while one reached by the tree models means their sampling and validation settings are too
 *  heavy, and telling the user to shrink the area there sends them the wrong way.
 * **Called by:
 * @see {mapGeeError}
 * @see {useErrorNotice} - so a caller can fall back to its own wording when nothing matches.
 * @memberof module:SHARED
 * @returns {String|null} The wording, or null when the failure says nothing recognisable.
 */
export const matchGeeError = (error, t, overrides = {}) => {
  const message = error?.message || (typeof error === 'string' ? error : '');
  const match = GEE_ERROR_PATTERNS.find(({ patterns }) => patterns.some((pattern) => pattern.test(message)));
  return match ? t(`${GEE_ERROR_NAMESPACE}.${overrides[match.key] ?? match.key}`) : null;
};

/**
 * @function mapGeeError
 * @description What to show the user for an Earth Engine failure: the wording for a recognised
 * failure, the raw message when it is short enough to read, and a generic line otherwise.
 * @param {Error|String} error - What Earth Engine threw.
 * @param {Function} t - Translation function.
 * @param {String} [fallbackKey] - Wording for a failure nothing recognises and whose own message is
 *  too technical to show.
 * @param {Object} [overrides] - Passed through to {@link matchGeeError}.
 * **Called by:
 * @see {PeriodChooser}
 * @see {BathymetryBandsAndAlgorithmSelection}
 * @memberof module:SHARED
 * @returns {String}
 */
export const mapGeeError = (error, t, fallbackKey = 'genericError', overrides = {}) => {
  const message = error?.message || (typeof error === 'string' ? error : '');
  const matched = matchGeeError(error, t, overrides);
  if (matched) return matched;
  const isTechnical = message.length > 120 || /Exception|Error:|at line/i.test(message);
  return isTechnical ? t(`${GEE_ERROR_NAMESPACE}.${fallbackKey}`) : message || t(`${GEE_ERROR_NAMESPACE}.${fallbackKey}`);
};

export const eeEval = (obj) =>
  new Promise((resolve, reject) => obj.evaluate((v, err) => (err ? reject(err) : resolve(v))));

/**
 * @function eeUrl
 * @description An image's thumbnail or download URL, asked for without freezing the page.
 * Without a callback the Earth Engine client sends `getThumbURL` and `getDownloadURL` as
 * synchronous requests and the page stops until the server answers - 9 to 18 s for the two URLs
 * of a composite of the whole Babitonga bay.
 * @param {Object} image - The `ee.Image`.
 * @param {'getThumbURL'|'getDownloadURL'} method - Which URL.
 * @param {Object} params - The method's parameters.
 * @memberof module:Shared
 * @returns {Promise<String>}
 */
export const eeUrl = (image, method, params) =>
  new Promise((resolve, reject) =>
    image[method](params, (url, err) => (err ? reject(err instanceof Error ? err : new Error(err)) : resolve(url))),
  );

export const REDUCERS = [
  { label: 'min', func: () => ee.Reducer.min() },
  { label: 'max', func: () => ee.Reducer.max() },
  { label: 'mean', func: () => ee.Reducer.mean() },
  { label: 'median', func: () => ee.Reducer.median() },
  { label: 'geomed', func: null },
];

export const clickDownloadArchive = async (e, url, nameArchive = 'cassie_image.png') => {
  e.preventDefault();
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = nameArchive;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading the image:', error);
  }
};

export const DATE_FORMATS = [
  {
    value: 'yyyy-MM-dd HH:mm:ss',
    label: '2025-06-10 14:30:00',
  },
  {
    value: 'yyyy-MM-dd HH:mm',
    label: '2025-06-10 14:30',
  },
  {
    value: 'dd/MM/yyyy HH:mm:ss',
    label: '10/06/2025 14:30:00',
  },
  {
    value: 'dd/MM/yyyy HH:mm',
    label: '10/06/2025 14:30',
  },
  {
    value: 'MM/dd/yyyy HH:mm:ss',
    label: '06/10/2025 14:30:00',
  },
  {
    value: 'MM/dd/yyyy HH:mm',
    label: '06/10/2025 14:30',
  },
];

/**
 * @function timer
 * @description Returns a Promise that resolves after the given delay.
 * @param {Number} ms - Delay in milliseconds.
 * **Called by:
 * @see {ProcessingPage} - Csqueeze : via startCalc, to throttle transect processing.
 * @returns {Promise<void>}
 */
export const timer = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * @function mapBiomasYear
 * @description Reads the classification year out of a land-cover tab key or a MapBiomas dataset
 * entry. The two are spelled differently on purpose: a tab is keyed `2018` or, once a year holds
 * more than one composite, `2018-1`; a dataset entry is a bare year in EUSCoast's collection and a
 * `classification_2018` band id in CSqueeze's. All of them carry the same four digits, so all are
 * read down to those before anything compares them or asks Earth Engine for a year - a raw tab key
 * reached the collection filter as `2018-1`, matched no image, and the null that came back failed
 * inside `Image.select`.
 * @param {String|Number} value - A tab key or a MapBiomas dataset entry.
 * **Called by:
 * @see {LandCover} - CSqueeze and EUSCoast, to tell whether MapBiomas covers the open tab and
 * which year to request for it.
 * @memberof module:SHARED
 * @returns {String|null} The four-digit year, or null when there is none.
 */
export const mapBiomasYear = (value) => {
  const match = String(value === null || value === undefined ? '' : value).match(/(\d{4})/);
  return match ? match[1] : null;
};

/**
 * @function formatPeriodRange
 * @description Formats the acquisition dates of one period as a `DD/MM/YY - DD/MM/YY` range, taken
 * from the first and the last image the period selected. A period holding a single image formats
 * as that one date.
 * The dates are read as the ISO text they are stored as rather than through `Date`, which parses a
 * bare `YYYY-MM-DD` as UTC and then hands back the previous day west of Greenwich - a period
 * acquired on the 1st would be labelled the month before.
 * @param {Array<{date: String}>} infos - The period's image infos, as `[key, infos]` entries hold.
 * **Called by:
 * @see {buildPeriodIndex} - to label the periods that share a year.
 * @memberof module:SHARED
 * @returns {String|null} The range, or null when the period has no readable date.
 */
export const formatPeriodRange = (infos) => {
  const dates = [...new Set((infos || []).map((info) => String(info?.date || '').slice(0, 10)))].filter(
    (date) => /^\d{4}-\d{2}-\d{2}$/.test(date),
  );
  if (!dates.length) return null;
  dates.sort();
  const format = (iso) => {
    const [year, month, day] = iso.split('-');
    return `${day}/${month}/${year.slice(-2)}`;
  };
  const first = format(dates[0]);
  const last = format(dates[dates.length - 1]);
  return first === last ? first : `${first} - ${last}`;
};

/**
 * @function buildPeriodIndex
 * @description Turns the per-period image selections CSqueeze and EUSCoast collect into the
 * `[key, infos]` entries their classification step and median-composite saga work in, keyed the
 * way the tabs are: the year on its own, and `<year>-1`, `<year>-2` for further periods inside a
 * year the user picked more than once. Alongside the entries it returns the label each key is
 * shown under.
 * The key stays the year and its suffix because everything downstream is arithmetic on it -
 * `mapBiomasYear` reads the four digits to ask Earth Engine for the classification year, the
 * result pages take `parseInt(key.slice(0, 4))` for the years' difference and order the periods
 * from the suffix. What a repeated `-1` is bad for is being read by a person: two periods of one
 * year are told apart by an index that says nothing about which is which. So the label, and only
 * the label, spells the year out as the period's own date range, and only for the years that
 * actually repeat - a lone year stays a year, as it always read.
 * @param {Array<Array<Object>>} periods - One list of image infos per confirmed period, as
 * `state.csqueeze.imageList` / `state.euscoast.imageList` hold them.
 * **Called by:
 * @see {handleConfirmElevation} - CSqueeze, to build the classification step's tabs.
 * @see {requestMedianImages} - EUSCoast, to build the classification step's tabs.
 * @see {importSessionState} - CSqueeze and EUSCoast, to rebuild the same layers.
 * @memberof module:SHARED
 * @returns {{entries: Array<Array>, labels: Object}} The `[key, infos]` pairs, newest period
 * first, and the `{key: label}` map to show them under.
 */
export const buildPeriodIndex = (periods) => {
  const timestamp = (date) => {
    const parsed = Date.parse(date);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const valid = (periods || []).filter((infos) => Array.isArray(infos) && infos.length && infos[0]?.date);
  const keyed = [...valid]
    .sort((a, b) => timestamp(b[0].date) - timestamp(a[0].date))
    .reduce((result, infos) => {
      const year = String(infos[0].date).slice(0, 4);
      const taken = Object.keys(result).filter((key) => key === year || key.startsWith(`${year}-`)).length;
      result[taken === 0 ? year : `${year}-${taken}`] = infos;
      return result;
    }, {});
  const entries = Object.entries(keyed);
  const perYear = entries.reduce((count, [key]) => {
    const year = key.slice(0, 4);
    count[year] = (count[year] || 0) + 1;
    return count;
  }, {});
  const labels = entries.reduce((acc, [key, infos]) => {
    acc[key] = perYear[key.slice(0, 4)] > 1 ? formatPeriodRange(infos) || key : key;
    return acc;
  }, {});
  return { entries, labels };
};

/**
 * @function periodFilenamePart
 * @description Rewrites a period label so it can go into a download's file name. The label of a
 * year picked twice is a `DD/MM/YY - DD/MM/YY` range, and its slashes are read as directory
 * separators by the browser, which either truncates the name or fails the download outright.
 * @param {String} label - A period label, or a period key.
 * **Called by:
 * @see {ProcessingPage} - CSqueeze and EUSCoast, to name the results CSV.
 * @memberof module:SHARED
 * @returns {String} The label with everything a file name should not hold reduced to dashes.
 */
export const periodFilenamePart = (label) =>
  String(label ?? '')
    .replace(/[^\w-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
